/**
 * <trade-in-form> — iPhone trade-in request flow (sections/trade-in.liquid).
 *
 * model (drawer) → storage → condition → details (name) → WhatsApp opens with the whole request
 * typed in → "sent" screen. Condition can branch to "refused" (Find My on / locked): no request.
 *
 * NO PRICES ON THE SITE (merchant decision 2026-11-03): the buy price depends on the resale
 * buyers TechBros works with and changes too often to publish, so staff reply in WhatsApp with
 * the offer in store credit and in cash. The page only collects model, storage and condition.
 *
 * WhatsApp is the only channel: nothing is posted or stored by the site. The send button is a
 * real <a target="_blank"> whose href (wa.me/<store>?text=…) is written in its own click handler,
 * so the browser opens it as a normal link — never popup-blocked, and iOS hands it to the app.
 * The request only reaches the store when the shopper presses Send in WhatsApp; photos are
 * attached there by the shopper (a website cannot attach files to a wa.me message), which is
 * why the message itself lists the photos we need. The reference is generated here, once per
 * request (changing the phone or the answers starts a new one).
 *
 * Models come from the section's "iPhone models" list (order, grouping, storages).
 *
 * Steps are mirrored into history.state so the browser/phone back button walks back through the
 * flow instead of leaving the page. Analytics: `on:trade-in:step` / `on:trade-in:submit` on document.
 */
class TradeInForm extends HTMLElement {
  constructor() {
    super();
    this.state = TradeInForm.emptyState();
    this.onPopState = this.onPopState.bind(this);
  }

  static emptyState() {
    return { model: '', storage: '', answers: {}, ref: '' };
  }

  connectedCallback() {
    this.config = TradeInForm.readJson(this.querySelector('[data-ti-config]')) || {};
    // `t` HTML-escapes its output (We&#39;ve); these strings are written with textContent, so decode once.
    const decoder = document.createElement('textarea');
    this.strings = Object.fromEntries(Object.entries(this.config.strings || {}).map(([key, value]) => {
      decoder.innerHTML = value;
      return [key, decoder.value];
    }));
    this.models = TradeInForm.buildModels(this.config.catalogue || '');
    this.drawer = this.querySelector('[data-ti-drawer]');

    this.steps = {};
    this.querySelectorAll('[data-ti-step]').forEach((el) => {
      this.steps[el.dataset.tiStep] = el;
    });

    this.renderDrawer();
    this.bindEvents();

    // Only claim history when we own the step; a fresh entry keeps the page's own back target intact.
    history.replaceState({ ...(history.state || {}), tiStep: 'model' }, '');
    window.addEventListener('popstate', this.onPopState);
    this.show('model', { push: false, focus: false });
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
  }

  static readJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  static fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
  }

  /** "iPhone 15 Pro" / "256GB" → comparable keys. */
  static key(text) {
    return String(text || '').toLowerCase().replace(/\s+/g, '');
  }

  /**
   * @param {string} catalogue  "Model | 128 GB, 256 GB" per line, newest first
   * @returns {{name: string, series: string, storages: string[]}[]}
   */
  static buildModels(catalogue) {
    const models = [];
    const byKey = new Map();
    String(catalogue).split(/\r?\n/).forEach((line) => {
      const [rawName, rawStorages = ''] = line.split('|');
      const name = (rawName || '').trim();
      const k = TradeInForm.key(name);
      if (!k) return;
      if (!byKey.has(k)) {
        const model = { name, storages: [] };
        byKey.set(k, model);
        models.push(model);
      }
      const model = byKey.get(k);
      rawStorages.split(',').forEach((s) => {
        const label = s.trim();
        if (label && !model.storages.some((x) => TradeInForm.key(x) === TradeInForm.key(label))) model.storages.push(label);
      });
    });

    // Group by the number in the name; a model without one (iPhone Air) joins the group above it.
    let series = '';
    models.forEach((model) => {
      const match = model.name.match(/(\d+)/);
      if (match) series = match[1];
      model.series = series;
    });
    return models;
  }

  modelByName(name) {
    return this.models.find((m) => m.name === name);
  }

  /* ---------- Rendering ---------- */

  renderDrawer() {
    const list = this.querySelector('[data-ti-drawer-list]');
    if (!this.models.length) {
      this.querySelector('[data-ti-empty]').hidden = false;
      this.querySelector('[data-ti-drawer-open]').hidden = true;
      return;
    }
    const groups = [];
    this.models.forEach((model) => {
      let group = groups[groups.length - 1];
      if (!group || group.series !== model.series) {
        group = { series: model.series, models: [] };
        groups.push(group);
      }
      group.models.push(model);
    });

    list.replaceChildren(...groups.map((group) => {
      const section = document.createElement('div');
      section.className = 'ti-drawer__group';
      section.dataset.tiGroup = '';
      if (group.series) {
        const heading = document.createElement('h3');
        heading.className = 'ti-drawer__group-title';
        heading.textContent = `iPhone ${group.series}`;
        section.append(heading);
      }
      group.models.forEach((model) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ti-drawer__item';
        btn.dataset.tiPick = 'model';
        btn.dataset.value = model.name;
        btn.dataset.search = TradeInForm.key(model.name);
        if (this.state.model === model.name) btn.setAttribute('aria-current', 'true');
        const name = document.createElement('span');
        name.className = 'ti-drawer__item-name';
        name.textContent = model.name;
        btn.append(name);
        section.append(btn);
      });
      return section;
    }));
  }

  filterDrawer(query) {
    const q = TradeInForm.key(query).replace(/^iphone/, '');
    let any = false;
    this.querySelectorAll('[data-ti-group]').forEach((group) => {
      let groupHit = false;
      group.querySelectorAll('.ti-drawer__item').forEach((item) => {
        const hit = !q || item.dataset.search.includes(q);
        item.hidden = !hit;
        groupHit = groupHit || hit;
      });
      group.hidden = !groupHit;
      any = any || groupHit;
    });
    this.querySelector('[data-ti-drawer-empty]').hidden = any;
  }

  openDrawer() {
    if (!this.drawer || this.drawer.open) return;
    const search = this.querySelector('[data-ti-drawer-search]');
    search.value = '';
    this.filterDrawer('');
    this.drawer.showModal();
    document.documentElement.classList.add('ti-drawer-open');
    // Own history entry, so the phone's back button closes the drawer instead of leaving the page.
    history.pushState({ ...(history.state || {}), tiStep: 'model', tiDrawer: true }, '');
    const current = this.drawer.querySelector('[aria-current="true"]');
    if (current) current.scrollIntoView({ block: 'center' });
  }

  /** User dismissal (×, Esc, backdrop): unwind the drawer's history entry; popstate closes it. */
  dismissDrawer() {
    if (history.state && history.state.tiDrawer) history.back();
    else this.closeDrawer();
  }

  closeDrawer() {
    if (this.drawer && this.drawer.open) this.drawer.close();
    document.documentElement.classList.remove('ti-drawer-open');
  }

  renderStorages() {
    const wrap = this.querySelector('[data-ti-storages]');
    const model = this.modelByName(this.state.model);
    wrap.replaceChildren(...(model ? model.storages : []).map((storage) => this.optionButton(storage, 'storage')));
  }

  optionButton(value, kind) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ti__option';
    btn.setAttribute('role', 'listitem');
    btn.dataset.tiPick = kind;
    btn.dataset.value = value;
    btn.textContent = value;
    if (this.state[kind] === value) btn.classList.add('is-selected');
    return btn;
  }

  /* ---------- Navigation ---------- */

  bindEvents() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-ti-drawer-open]')) {
        this.openDrawer();
        return;
      }
      if (event.target.closest('[data-ti-drawer-close]') || event.target === this.drawer) {
        // A click on the <dialog> itself (not its panel) is a click on the backdrop.
        this.dismissDrawer();
        return;
      }
      const pick = event.target.closest('[data-ti-pick]');
      if (pick) {
        this.pick(pick.dataset.tiPick, pick.dataset.value);
        return;
      }
      if (event.target.closest('[data-ti-back]')) {
        history.back();
        return;
      }
      if (event.target.closest('[data-ti-condition-next]')) this.submitCondition();
      if (event.target.closest('[data-ti-restart]')) this.restart();
      // "trade-in terms" link → open the collapsed terms it points at.
      const termsLink = event.target.closest('a[href^="#ti-terms"]');
      if (termsLink) {
        const terms = document.querySelector(termsLink.getAttribute('href'));
        if (terms) {
          // No hash navigation: it would add a history entry the in-flow Back button then has to undo.
          event.preventDefault();
          terms.open = true;
          terms.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    });

    if (this.drawer) {
      this.drawer.addEventListener('cancel', (event) => {
        event.preventDefault(); // Esc: go through history like every other dismissal
        this.dismissDrawer();
      });
      this.drawer.addEventListener('close', () => document.documentElement.classList.remove('ti-drawer-open'));
      this.querySelector('[data-ti-drawer-search]').addEventListener('input', (event) => this.filterDrawer(event.target.value));
    }

    this.querySelectorAll('[data-ti-question] input').forEach((input) => {
      input.addEventListener('change', () => {
        this.state.answers[input.closest('[data-ti-question]').dataset.tiQuestion] = input.value;
        this.state.ref = '';
        const error = this.querySelector('[data-ti-condition-error]');
        if (error) error.hidden = true;
      });
    });

    this.form = this.querySelector('[data-ti-form]');
    this.sendLink = this.querySelector('[data-ti-send]');
    this.sendLink.addEventListener('click', (event) => this.handleSend(event));
    // Enter in the name field: same path as tapping the button (a real click keeps the user gesture).
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.sendLink.click();
    });
    this.form.querySelectorAll('[data-ti-required], [data-ti-terms]').forEach((input) => {
      input.addEventListener('input', () => this.clearFieldError(input));
      input.addEventListener('change', () => this.clearFieldError(input));
    });
  }

  pick(kind, value) {
    if (kind === 'model') {
      if (this.state.model !== value) {
        this.state.storage = '';
        this.state.ref = '';
      }
      this.state.model = value;
      this.querySelector('[data-ti-picker-value]').textContent = value;
      this.querySelector('[data-ti-drawer-open]').classList.add('has-value');
      this.renderDrawer();
      this.renderStorages();
      if (this.drawer && this.drawer.open) {
        // Turn the drawer's history entry into the storage step: Back from storage → model.
        this.closeDrawer();
        history.replaceState({ ...(history.state || {}), tiStep: 'storage', tiDrawer: false }, '');
        this.show('storage', { push: false });
      } else {
        this.show('storage');
      }
    } else if (kind === 'storage') {
      if (this.state.storage !== value) this.state.ref = '';
      this.state.storage = value;
      this.renderStorages();
      const summary = `${this.state.model}, ${value}`;
      this.querySelector('[data-ti-selected]').textContent = summary;
      this.querySelector('[data-ti-details-summary]').textContent = summary;
      this.show('condition');
    }
  }

  show(name, { push = true, focus = true } = {}) {
    Object.entries(this.steps).forEach(([key, el]) => {
      el.hidden = key !== name;
    });
    this.current = name;
    if (push) history.pushState({ ...(history.state || {}), tiStep: name }, '');
    this.updateProgress(name);

    if (focus) {
      const title = this.steps[name].querySelector('.ti__step-title');
      if (title) title.focus({ preventScroll: true });
      const top = this.getBoundingClientRect().top + window.scrollY - (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0) - 16;
      if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
    }

    document.dispatchEvent(new CustomEvent('on:trade-in:step', {
      detail: { step: name, model: this.state.model, storage: this.state.storage },
    }));
  }

  onPopState(event) {
    this.closeDrawer();
    if (event.state && event.state.tiDrawer) return;
    const step = event.state && event.state.tiStep;
    if (!step || !this.steps[step]) return;
    // Back from "sent" returns to the details step (nothing was submitted by the site, so the
    // shopper may fix their name and send again with the same reference).
    this.show(this.canShow(step) ? step : 'model', { push: false });
  }

  static answered(a) {
    return ['screen', 'body', 'function', 'battery', 'lock'].every((key) => a[key]);
  }

  canShow(step) {
    const { model, storage, answers, ref } = this.state;
    const ready = !!(model && storage) && TradeInForm.answered(answers);
    switch (step) {
      case 'model': return true;
      case 'storage': return !!model;
      case 'condition': return !!(model && storage);
      case 'details': return ready && answers.lock === 'yes';
      case 'refused': return ready && answers.lock !== 'yes';
      case 'success': return ready && answers.lock === 'yes' && !!ref;
      default: return false;
    }
  }

  updateProgress(name) {
    const order = ['model', 'storage', 'condition', 'details'];
    const index = order.indexOf(name);
    const bar = this.querySelector('[data-ti-progress]');
    if (!bar) return;
    bar.hidden = index === -1;
    if (index === -1) return;
    this.querySelector('[data-ti-progress-label]').textContent = TradeInForm.fill(this.strings.stepOf, { current: index + 1, total: order.length });
    this.querySelector('[data-ti-progress-fill]').style.width = `${((index + 1) / order.length) * 100}%`;
  }

  /* ---------- Condition ---------- */

  /** Find My / finance / carrier lock is a hard gate (we can't buy the phone); everything else goes to WhatsApp. */
  submitCondition() {
    const a = this.state.answers;
    if (!TradeInForm.answered(a)) {
      this.querySelector('[data-ti-condition-error]').hidden = false;
      const first = ['screen', 'body', 'function', 'battery', 'lock'].find((key) => !a[key]);
      const input = this.querySelector(`[data-ti-question="${first}"] input`);
      if (input) input.focus();
      return;
    }
    this.show(a.lock === 'yes' ? 'details' : 'refused');
  }

  /* ---------- Details form ---------- */

  fieldError(input) {
    const field = input.closest('.ti__field');
    return field ? field.querySelector('[data-ti-field-error]') : null;
  }

  setFieldError(input, message) {
    const el = this.fieldError(input);
    if (el) {
      el.textContent = message;
      el.hidden = false;
    }
    input.setAttribute('aria-invalid', 'true');
  }

  clearFieldError(input) {
    const el = this.fieldError(input);
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
    input.removeAttribute('aria-invalid');
  }

  validate() {
    let firstInvalid = null;
    const fail = (input, message) => {
      this.setFieldError(input, message);
      if (!firstInvalid) firstInvalid = input;
    };

    this.form.querySelectorAll('[data-ti-required]').forEach((input) => {
      if (!input.value.trim()) fail(input, this.strings.required);
      else this.clearFieldError(input);
    });

    const terms = this.form.querySelector('[data-ti-terms]');
    if (!terms.checked) fail(terms, this.strings.termsRequired);
    else this.clearFieldError(terms);

    return firstInvalid;
  }

  static reference() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const bytes = new Uint8Array(4);
    (window.crypto || {}).getRandomValues ? window.crypto.getRandomValues(bytes) : bytes.forEach((_, i) => { bytes[i] = Math.random() * 256; });
    const suffix = Array.from(bytes, (b) => chars[b % chars.length]).join('');
    return `TB-TI-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${suffix}`;
  }

  waLink(message) {
    return `https://wa.me/${this.config.waNumber}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
  }

  /** The full request as the store reads it in WhatsApp, in the page language. */
  waRequest(name) {
    const s = this.strings;
    const a = this.state.answers;
    const yesNo = (v) => (v === 'yes' ? s.yes : s.no);
    const battery = { 85: s.battery85, 80: s.battery80, low: s.batteryLow, unknown: s.batteryUnknown }[a.battery] || '';
    return TradeInForm.fill(s.waMessage, {
      ref: this.state.ref,
      name,
      model: this.state.model,
      storage: this.state.storage,
      screen: yesNo(a.screen),
      body: yesNo(a.body),
      function: yesNo(a.function),
      battery,
      lock: yesNo(a.lock),
      photos: s.photos || '',
    });
  }

  /**
   * Click on the send link. Invalid → stop the navigation. Valid → write the wa.me href now (the
   * browser follows the href as it is when the handler returns) and move to the "sent" step.
   */
  handleSend(event) {
    if (!this.canShow('details')) {
      event.preventDefault();
      return;
    }
    const firstInvalid = this.validate();
    if (firstInvalid) {
      event.preventDefault();
      firstInvalid.focus();
      return;
    }

    if (!this.state.ref) this.state.ref = TradeInForm.reference();
    const name = new FormData(this.form).get('name').trim();
    const href = this.waLink(this.waRequest(name));
    this.sendLink.href = href;
    this.querySelector('[data-ti-wa-success]').href = href;
    this.querySelector('[data-ti-ref]').textContent = this.state.ref;

    document.dispatchEvent(new CustomEvent('on:trade-in:submit', {
      detail: { reference: this.state.ref, model: this.state.model, storage: this.state.storage },
    }));
    // After the link has been followed: switching steps inside the handler would hide the link mid-click.
    setTimeout(() => this.show('success'), 0);
  }

  /** "Trade in another iPhone": clear everything and start again at the model step. */
  restart() {
    this.state = TradeInForm.emptyState();
    this.querySelectorAll('[data-ti-question] input').forEach((input) => {
      input.checked = false;
    });
    this.form.reset();
    const picker = this.querySelector('[data-ti-picker-value]');
    picker.textContent = picker.dataset.placeholder;
    this.querySelector('[data-ti-drawer-open]').classList.remove('has-value');
    this.renderDrawer();
    this.show('model');
  }
}

customElements.define('trade-in-form', TradeInForm);
